/**
 * EmirateDocsSection — per-emirate required documents for one service.
 * Emirate chips (with counts) + copy bar + the ServiceRequiredDocs toggles for the selected emirate.
 * `lockedEmirate` hides the chips (used inside the emirate card dialog).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ServiceRequiredDocs } from '../ServiceRequiredDocs';
import EmirateDocsCopyBar from './EmirateDocsCopyBar';
import { EMIRATES, normalizePricing } from './serviceEmirates';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const fetchDocsSummary = (serviceId) =>
  axios.get(`${API_URL}/api/services/${serviceId}/required-docs/emirates-summary`, { headers: getAuthHeaders() })
    .then((r) => r.data?.summary || {});

export const emirateLabel = (key, isAr) => { const e = EMIRATES.find((x) => x.key === key); return e ? (isAr ? e.ar : e.en) : key; };

export default function EmirateDocsSection({ service, language, lockedEmirate = null, testIdPrefix = 'svc', onSummaryChange }) {
  const isAr = language === 'ar';
  const pricing = useMemo(() => normalizePricing(service?.emirate_pricing), [service]);
  const firstEnabled = pricing.dubai?.enabled ? 'dubai' : (EMIRATES.find((e) => pricing[e.key].enabled)?.key || 'dubai');
  const [emirate, setEmirate] = useState(lockedEmirate || firstEnabled);
  const [summary, setSummary] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(async () => {
    if (!service?.id) return;
    try { const s = await fetchDocsSummary(service.id); setSummary(s); onSummaryChange?.(s); } catch { /* ignore */ }
  }, [service?.id, onSummaryChange]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (lockedEmirate) setEmirate(lockedEmirate); }, [lockedEmirate]);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-emirate-docs`}>
      {!lockedEmirate && (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-emirate-docs-chips`}>
          {EMIRATES.map((e) => {
            const on = pricing[e.key].enabled; const active = emirate === e.key; const n = summary[e.key] || 0;
            return (
              <button key={e.key} type="button" onClick={() => setEmirate(e.key)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors flex items-center gap-2 ${active ? 'bg-[#c9a227] text-black border-[#c9a227]' : on ? 'bg-[#1a1a2e] text-white border-white/15 hover:border-[#c9a227]/50' : 'bg-transparent text-white/40 border-white/10 hover:text-white/70'}`}
                data-testid={`${testIdPrefix}-docs-chip-${e.key}`}>
                {emirateLabel(e.key, isAr)}
                <span className={`px-1.5 rounded-full text-[10px] ${active ? 'bg-black/20 text-black' : n ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/50'}`} data-testid={`${testIdPrefix}-docs-count-${e.key}`}>{n}</span>
                {!on && <span className="text-[10px]">{isAr ? '· غير مفعّلة' : '· off'}</span>}
              </button>
            );
          })}
        </div>
      )}
      <EmirateDocsCopyBar serviceId={service.id} emirate={emirate} summary={summary} language={language} testIdPrefix={testIdPrefix}
        onCopied={() => { setReloadKey((k) => k + 1); refresh(); }} />
      <ServiceRequiredDocs serviceId={service.id} serviceLabel={service.name_ar} serviceLabelEn={service.name_en}
        emirate={emirate} emirateLabel={emirateLabel(emirate, isAr)} reloadKey={reloadKey} onChanged={refresh} />
    </div>
  );
}
